import { useEffect } from 'react';
import Link from 'next/link';
import {
  Badge,
  Card,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  PaymentMethod,
  ServiceMethod,
} from '@/src/models/marketplace-service-info';
import { useOutletServices } from '@/src/modules/outlets/hooks/use-outlet-services';
import { toTitleCase } from '../../utils/common';
import AnyError from '../produk-kesehatan/AnyError';
import SearchNotFound from '../produk-kesehatan/SearchNotFound';
import { LayananSkeleton } from './LayananSkeleton';

type LayananProps = {
  outlet_id: number;
  offset: number;
  size: number;
  search?: string;
  totalData?: (total: number, per_page: number) => void;
  goToLayanan?: () => void;
  showHeader?: boolean;
};
export function Layanan({
  outlet_id,
  offset,
  size,
  search,
  totalData,
  goToLayanan,
  showHeader,
}: LayananProps) {
  function checkBadge(row: any) {
    const badges = [];
    if (row.service_method?.includes(ServiceMethod.HomeCare)) {
      badges.push({ label: 'Home Care', color: 'blue', variant: 'light' });
    }
    if (row.service_method?.includes(ServiceMethod.Online)) {
      badges.push({ label: 'Online', color: 'cyan', variant: 'light' });
    }
    if (row.service_method?.includes(ServiceMethod.Offline)) {
      badges.push({ label: 'Offline', color: 'red', variant: 'light' });
    }
    if (row.payment_method?.includes(PaymentMethod.Umum)) {
      badges.push({ label: 'Umum', color: 'green', variant: 'light' });
    }
    if (row.payment_method?.includes(PaymentMethod.Bpjs)) {
      badges.push({ label: 'BPJS', color: 'green', variant: 'light' });
    }
    if (row.payment_method?.includes(PaymentMethod.Asuransi)) {
      badges.push({ label: 'Asuransi', color: 'green', variant: 'light' });
    }

    return badges;
  }

  const ITEMS_PER_PAGE = 4;
  const { data, error, isLoading } = useOutletServices({
    outlet_id,
    offset,
    size,
    search,
  });

  useEffect(() => {
    if (data?.meta?.total !== undefined && totalData) {
      totalData(data.meta.total, data.meta.per_page_size);
    }
  });

  const isEmpty = !isLoading && data?.data.length === 0;
  const renderContent = () => {
    if (showHeader && (isEmpty || search || error)) {
      return null;
    }

    if (isEmpty && search) {
      return <SearchNotFound />;
    }

    if (isEmpty) {
      return <SearchNotFound />;
    }

    if (error) {
      return <AnyError />;
    }

    if (data?.data.length === 0) {
      return null;
    }

    return (
      <Stack>
        {showHeader && (
          <Group justify="space-between">
            <Title order={3} fz={{ base: 'lg', md: 'xl', lg: '1.5rem' }}>
              Layanan Rekomendasi
            </Title>
            <Text
              variant="subtle"
              fz={{ base: 'xs', md: 'lg' }}
              c="#026D77"
              onClick={goToLayanan}
              style={{ cursor: 'pointer' }}
            >
              Lihat Semua
            </Text>
          </Group>
        )}
        <SimpleGrid
          cols={{ base: 2, sm: 4 }}
          spacing={{ base: 'sm', lg: 'lg' }}
        >
          {isLoading
            ? Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                <LayananSkeleton key={i} />
              ))
            : data?.data.map((item) => (
                <Card
                  key={item.bpis_id}
                  padding="0"
                  radius="md"
                  mt="md"
                  component={Link}
                  href={`/detail-item/services/${item.bpis_id}?individual_id=${item.individual_id}`}
                  className="cursor-pointer"
                  withBorder
                >
                  <Image
                    src={item.doctor_image}
                    h={{ base: '140', sm: '220' }}
                    w="100%"
                    fit="contain"
                    alt={item.doctor_name}
                    fallbackSrc="/images/logo.png"
                    className="rounded-t-md"
                  />
                  <Stack gap="xs" my="sm" px={{ base: 'sm', md: 'md' }}>
                    <Group
                      gap="0.25rem"
                      className="min-h-10"
                      align="start"
                      my="sm"
                    >
                      {checkBadge(item).map((badge) => (
                        <Badge
                          key={badge.label}
                          color={badge.color}
                          variant={badge.variant as any}
                          size="sm"
                        >
                          {badge.label}
                        </Badge>
                      ))}
                      {item.is_event && (
                        <Badge color="yellow" variant="light" size="sm">
                          Event
                        </Badge>
                      )}
                    </Group>

                    <Stack gap="0.25rem">
                      <Stack gap={0}>
                        <Text lineClamp={2} fz="sm" fw={500}>
                          {toTitleCase(item.service_name)}
                        </Text>

                        <Text c="gray.6" fz="xs" lineClamp={1}>
                          Oleh {toTitleCase(item.doctor_name)}
                        </Text>
                      </Stack>
                      <Text fw="bold" fz="sm" className="min-h-10">
                        {item.price}
                      </Text>
                    </Stack>
                  </Stack>
                </Card>
              ))}
        </SimpleGrid>
      </Stack>
    );
  };
  return <Stack>{renderContent()}</Stack>;
}
